/**
 * The Cash Drop submission form (phase 6 doc). Fields mirror
 * `CashDropSubmissionIn` exactly - no field exists here that the backend
 * model does not also validate, and `submitted_by` has no field at all: it is
 * bound server-side to the signed-in principal.
 *
 * Photos are upload-first (phase 5 doc, "How attachment_ids maps to stored
 * files"): each of the three slots uploads the moment a file is chosen, not
 * when the form is submitted, and Submit only sends the ids those uploads
 * returned. The slot machinery is the shared `AttachmentSlots` (consolidated
 * 2026-09-07; this form carried the original inline copy). Nothing here
 * computes a money value - see `package.json`'s description; `till`, `tip`
 * and `total` travel as the strings the manager typed, exactly as the
 * backend stores them (Decimal text).
 */
import { useState, type FormEvent } from "react";

import {
  LOCATIONS,
  submitCashDrop,
  uploadAttachment,
  type CashDropRecord,
  type Location,
} from "../api/public";
import type { TokenGetter } from "../auth/session";
import {
  AttachmentSlots,
  MAX_SLOTS,
  errorMessage,
  useAttachmentSlots,
} from "../components/AttachmentSlots";
import { SessionGate } from "../components/SessionGate";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Trims, folds a comma decimal separator to a period, and adds a leading
 * zero to a bare decimal - iOS's decimal keypad renders a comma depending on
 * the device's region, and produces a leading-dot value (`.01`) naturally
 * (there is no "0" key press before it), so both are normalized here, before
 * both the validity check and the value actually sent. The backend's
 * `Decimal` field would accept a raw `.01` fine (Python's `Decimal(".01")`
 * parses it), so this is purely for `isValidMoney` and consistency, not a
 * gap the backend leaves open. */
function normalizeMoney(value: string): string {
  const folded = value.trim().replace(",", ".");
  return folded.startsWith(".") ? `0${folded}` : folded;
}

/** A non-negative decimal, in the plain digits-and-dot form the field accepts
 * (`Decimal, Field(ge=0)`); no scientific notation, no leading sign, so what
 * the manager typed (after `normalizeMoney`) is exactly what gets sent. */
function isValidMoney(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(normalizeMoney(value));
}

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; record: CashDropRecord }
  | { status: "error"; message: string };

export function SubmitCashDrop() {
  return <SessionGate>{(getToken) => <SubmitCashDropForm getToken={getToken} />}</SessionGate>;
}

function SubmitCashDropForm({ getToken }: { getToken: TokenGetter }) {
  const [location, setLocation] = useState<Location | "">("");
  const [dropDate, setDropDate] = useState(todayIso());
  const [till, setTill] = useState("");
  const [tip, setTip] = useState("");
  const [bankDeposit, setBankDeposit] = useState("");
  const [total, setTotal] = useState("");
  const uploads = useAttachmentSlots(uploadAttachment, getToken);
  const [submission, setSubmission] = useState<SubmitState>({ status: "idle" });

  const { attachmentIds, anyUploading } = uploads;

  // Submit is never hard-disabled on field validity - a disabled button with
  // no visual `:disabled` styling looked identical to an enabled one and
  // silently ate every tap (2026-09-07 iPhone Safari incident: a comma
  // decimal separator failed `isValidMoney` with no error shown anywhere).
  // Instead this runs on click and surfaces every problem inline, the same
  // `submission.status === "error"` panel a server rejection uses.
  function validationErrors(): string[] {
    const errors: string[] = [];
    if (location === "") errors.push("Choose a location.");
    if (dropDate.trim() === "") errors.push("Enter the drop date.");
    if (!isValidMoney(till)) errors.push("Enter a valid Total cash (Z-report) amount.");
    if (!isValidMoney(tip)) errors.push("Enter a valid Tip amount.");
    if (!isValidMoney(bankDeposit)) errors.push("Enter a valid Bank deposit amount.");
    if (!isValidMoney(total)) errors.push("Enter a valid Total amount.");
    if (attachmentIds.length < 1) errors.push("Attach at least one photo.");
    if (attachmentIds.length > MAX_SLOTS) errors.push(`Attach at most ${MAX_SLOTS} photos.`);
    if (anyUploading) errors.push("Wait for the photo upload to finish.");
    return errors;
  }

  function resetForm() {
    setLocation("");
    setDropDate(todayIso());
    setTill("");
    setTip("");
    setBankDeposit("");
    setTotal("");
    uploads.reset();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submission.status === "submitting") return; // guards against a double tap
    const errors = validationErrors();
    // `location === ""` is folded into this guard (already implied whenever
    // `errors` is non-empty) purely so TypeScript's control-flow analysis
    // narrows `location` from `Location | ""` to `Location` below - the same
    // trick the old `formValid` boolean relied on.
    if (errors.length > 0 || location === "") {
      setSubmission({ status: "error", message: errors.join(" ") });
      return;
    }
    setSubmission({ status: "submitting" });
    try {
      const token = await getToken();
      const record = await submitCashDrop(
        {
          location,
          drop_date: dropDate,
          till: normalizeMoney(till),
          tip: normalizeMoney(tip),
          bank_deposit_amount: normalizeMoney(bankDeposit),
          total: normalizeMoney(total),
          attachment_ids: attachmentIds,
        },
        token,
      );
      setSubmission({ status: "success", record });
      resetForm();
    } catch (error) {
      setSubmission({ status: "error", message: errorMessage(error) });
    }
  }

  return (
    <>
      {submission.status === "success" && (
        <section className="card success" data-testid="submit-success">
          <h2>Submitted</h2>
          <dl className="facts">
            <dt>Location</dt>
            <dd>{submission.record.location}</dd>
            <dt>Drop date</dt>
            <dd>{submission.record.drop_date}</dd>
            <dt>Total cash</dt>
            <dd>{submission.record.till}</dd>
            <dt>Tip</dt>
            <dd>{submission.record.tip}</dd>
            <dt>Bank deposit</dt>
            <dd>{submission.record.bank_deposit_amount}</dd>
            <dt>Undeposited</dt>
            <dd data-testid="undeposited-value">{submission.record.undeposited_balance}</dd>
          </dl>
        </section>
      )}

      {/* noValidate: our own `validationErrors()` is the one source of truth
          for what's wrong - the browser's native required/pattern validation
          would otherwise silently swallow the submit event for an empty
          required field before our handler (and its inline message) ever
          runs, reintroducing the same silent no-op this form's `disabled`
          removal was meant to fix. */}
      <form className="card" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <h2>Cash Drop</h2>

        {submission.status === "error" && (
          <p className="field-error" role="alert" data-testid="submit-error">
            {submission.message}
          </p>
        )}

        <label className="field">
          Location
          <select value={location} onChange={(e) => setLocation(e.target.value as Location)} required>
            <option value="" disabled>
              Choose a location
            </option>
            {LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Drop date
          <input
            type="date"
            value={dropDate}
            onChange={(e) => setDropDate(e.target.value)}
            required
          />
        </label>

        <label className="field">
          Total cash (Z-report)
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={till}
            onChange={(e) => setTill(e.target.value)}
            required
          />
        </label>
        <p className="muted" data-testid="till-field-hint">
          Enter the total cash in your drawer per your Z-report — not your starting float.{" "}
          Enter the exact amount from your Toast Z-report. Do not round.
        </p>

        <label className="field">
          Tip
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            required
          />
        </label>

        <label className="field">
          Bank deposit (exact amount in the envelope)
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={bankDeposit}
            onChange={(e) => setBankDeposit(e.target.value)}
            required
          />
        </label>

        <label className="field">
          Total
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            required
          />
        </label>

        <AttachmentSlots
          legend="Photos (1-3 required)"
          slots={uploads.slots}
          onChange={uploads.handleSlotInputChange}
          onClear={uploads.clearSlot}
        />

        <button type="submit" disabled={submission.status === "submitting"}>
          {submission.status === "submitting" ? "Submitting…" : "Submit"}
        </button>
      </form>
    </>
  );
}
